import {
  BULK_ASSIGN_BY_SCOPE_URL,
  BULK_ASSIGN_QUESTIONS_URL,
  BULK_UNASSIGN_QUESTIONS_URL,
  GET_ASSIGNED_QUESTIONS_FOR_RULE_URL,
  GET_COMPLIANCE_MAIN_SECTIONS_URL,
  GET_COMPLIANCE_QUESTIONS_URL,
  GET_COMPLIANCE_SUB_SECTIONS_URL,
  GET_ENTITY_TYPE_FORMS_URL,
  get_headers,
} from "@/utils/constants/constants";
import alert_mixin from "@/utils/mixins/alert_mixin";
import axios from "axios";

export default {
  name: "ComplianceQuestionAssignment",
  mixins: [alert_mixin],

  props: {
    selected_rule: { type: Object, default: null },
    entity_type_id: { type: [String, Number], required: true },
    is_visible: { type: Boolean, default: false },
  },

  emits: ["close", "updated"],

  data: () => ({
    headers: get_headers(),

    // Left panel - form hierarchy selection
    available_forms: [],
    selected_form_id: "",
    is_loading_forms: false,

    main_sections: [],
    selected_main_section_id: "",
    is_loading_main_sections: false,

    sub_sections: [],
    selected_sub_section_id: "",
    is_loading_sub_sections: false,

    question_list: [],
    question_search: "",
    is_loading_questions: false,
    is_bulk_assigning: false,

    // Right panel - assigned questions (nested data from API)
    assigned_forms: [],
    is_loading_assigned: false,
    assigned_search: "",

    // Tree view collapse state
    collapsed: Object.create(null),
  }),

  computed: {
    filtered_questions() {
      const search_term = this.question_search.toLowerCase().trim();
      if (!search_term) {
        return this.question_list;
      }
      return this.question_list.filter(
        (section_question) =>
          Reflect.get(section_question, "question")?.question_text?.toLowerCase().includes(search_term) ||
          Reflect.get(section_question, "question")?.question_type?.toLowerCase().includes(search_term)
      );
    },

    current_question_ids() {
      return this.question_list.map((section_question) => Reflect.get(section_question, "id"));
    },

    total_assigned() {
      let count = 0;
      for (const form of this.assigned_forms) {
        for (const main_section of Reflect.get(form, "main_sections")) {
          for (const sub_section of Reflect.get(main_section, "sub_sections")) {
            count += Reflect.get(sub_section, "questions").length;
          }
        }
      }
      return count;
    },

    flat_assigned_questions() {
      const flat_list = [];
      for (const form of this.assigned_forms) {
        for (const main_section of Reflect.get(form, "main_sections")) {
          for (const sub_section of Reflect.get(main_section, "sub_sections")) {
            for (const question of Reflect.get(sub_section, "questions")) {
              flat_list.push({
                ...question,
                form_name: Reflect.get(form, "form_name"),
                main_section_name: Reflect.get(main_section, "main_section_name"),
                sub_section_name: Reflect.get(sub_section, "sub_section_name"),
              });
            }
          }
        }
      }
      return flat_list;
    },

    filtered_assigned_questions() {
      const search_term = this.assigned_search.toLowerCase().trim();
      if (!search_term) {
        return [];
      }
      return this.flat_assigned_questions.filter(
        (question) =>
          Reflect.get(question, "question_text")?.toLowerCase().includes(search_term) ||
          Reflect.get(question, "form_name")?.toLowerCase().includes(search_term) ||
          Reflect.get(question, "main_section_name")?.toLowerCase().includes(search_term) ||
          Reflect.get(question, "sub_section_name")?.toLowerCase().includes(search_term)
      );
    },
  },

  watch: {
    is_visible(is_now_visible) {
      if (is_now_visible && this.selected_rule) {
        this.initialize();
      }
    },
  },

  methods: {
    initialize() {
      this.reset_state();
      this.load_forms();
      this.load_assigned_questions();
    },

    reset_state() {
      this.question_search = "";
      this.assigned_search = "";
      this.selected_form_id = "";
      this.selected_main_section_id = "";
      this.selected_sub_section_id = "";
      this.main_sections = [];
      this.sub_sections = [];
      this.question_list = [];
      this.collapsed = Object.create(null);
    },

    toggle_collapse(collapse_key) {
      Reflect.set(this.collapsed, collapse_key, !Reflect.get(this.collapsed, collapse_key));
    },

    is_collapsed(collapse_key) {
      return !!Reflect.get(this.collapsed, collapse_key);
    },

    load_forms() {
      if (!this.entity_type_id) {
        this.available_forms = [];
        return;
      }
      this.is_loading_forms = true;
      axios
        .get(GET_ENTITY_TYPE_FORMS_URL, {
          headers: this.headers,
          params: { entity_type_id: this.entity_type_id },
        })
        .then((response) => {
          this.available_forms = Reflect.get(response.data, "status") === "success" ? Reflect.get(response.data, "data") : [];
        })
        .catch((error) => {
          this.show_error("Failed to load forms: " + Reflect.get(error, "message"));
          this.available_forms = [];
        })
        .finally(() => {
          this.is_loading_forms = false;
        });
    },

    load_main_sections() {
      this.selected_main_section_id = "";
      this.sub_sections = [];
      this.question_list = [];

      if (!this.selected_form_id) {
        this.main_sections = [];
        return;
      }
      this.is_loading_main_sections = true;
      axios
        .get(GET_COMPLIANCE_MAIN_SECTIONS_URL, {
          headers: this.headers,
          params: { form_id: this.selected_form_id },
        })
        .then((response) => {
          this.main_sections = Reflect.get(response.data, "status") === "success" ? Reflect.get(response.data, "data") : [];
        })
        .catch((error) => {
          this.show_error("Failed to load main sections: " + Reflect.get(error, "message"));
          this.main_sections = [];
        })
        .finally(() => {
          this.is_loading_main_sections = false;
        });
    },

    load_sub_sections() {
      this.selected_sub_section_id = "";
      this.question_list = [];

      if (!this.selected_main_section_id) {
        this.sub_sections = [];
        return;
      }
      this.is_loading_sub_sections = true;
      axios
        .get(GET_COMPLIANCE_SUB_SECTIONS_URL, {
          headers: this.headers,
          params: { form_main_section_id: this.selected_main_section_id },
        })
        .then((response) => {
          this.sub_sections = Reflect.get(response.data, "status") === "success" ? Reflect.get(response.data, "data") : [];
        })
        .catch((error) => {
          this.show_error("Failed to load sub-sections: " + Reflect.get(error, "message"));
          this.sub_sections = [];
        })
        .finally(() => {
          this.is_loading_sub_sections = false;
        });
    },

    load_questions() {
      if (!this.selected_sub_section_id) {
        this.question_list = [];
        return;
      }
      this.is_loading_questions = true;
      axios
        .get(GET_COMPLIANCE_QUESTIONS_URL, {
          headers: this.headers,
          params: { form_section_id: this.selected_sub_section_id },
        })
        .then((response) => {
          if (Reflect.get(response.data, "status") === "success") {
            this.question_list = (Reflect.get(response.data, "data") || []).map((section_question) => ({
              ...section_question,
              is_assigned: this.check_if_assigned(Reflect.get(section_question, "id")),
              is_loading: false,
            }));
          }
        })
        .catch((error) => {
          this.show_error("Failed to load questions: " + Reflect.get(error, "message"));
          this.question_list = [];
        })
        .finally(() => {
          this.is_loading_questions = false;
        });
    },

    load_assigned_questions() {
      if (!this.selected_rule?.id) {
        this.assigned_forms = [];
        return;
      }
      this.is_loading_assigned = true;
      axios
        .get(GET_ASSIGNED_QUESTIONS_FOR_RULE_URL, {
          headers: this.headers,
          params: { compliance_rule_id: Reflect.get(this.selected_rule, "id") },
        })
        .then((response) => {
          this.assigned_forms = Reflect.get(response.data, "status") === "success" ? Reflect.get(response.data, "data") : [];
        })
        .catch((error) => {
          this.show_error("Failed to load assigned questions: " + Reflect.get(error, "message"));
          this.assigned_forms = [];
        })
        .finally(() => {
          this.is_loading_assigned = false;
        });
    },

    check_if_assigned(section_question_id) {
      return this.flat_assigned_questions.some(
        (question) => Reflect.get(question, "id") === section_question_id
      );
    },

    assign_all_sub_section_questions() {
      if (!this.selected_sub_section_id || !this.selected_rule?.id) {
        return;
      }
      this.assign_by_scope("sub_section", this.selected_sub_section_id);
    },

    assign_all_main_section_questions() {
      if (!this.selected_main_section_id || !this.selected_rule?.id) {
        return;
      }
      this.assign_by_scope("main_section", this.selected_main_section_id);
    },

    assign_all_form_questions() {
      if (!this.selected_form_id || !this.selected_rule?.id) {
        return;
      }
      this.assign_by_scope("form", this.selected_form_id);
    },

    assign_by_scope(scope, scope_id) {
      this.is_bulk_assigning = true;
      axios
        .post(
          BULK_ASSIGN_BY_SCOPE_URL,
          {
            compliance_rule_id: Reflect.get(this.selected_rule, "id"),
            scope: scope,
            scope_id: scope_id,
          },
          { headers: this.headers }
        )
        .then((response) => {
          if (Reflect.get(response.data, "status") === "success") {
            const scope_label = scope.replace("_", " ");
            this.show_success(`Assigned ${Reflect.get(response.data, "processed_count")} questions from ${scope_label}`);
            this.refresh_data();
          } else {
            this.show_error(Reflect.get(response.data, "message") || "Failed to assign questions");
          }
        })
        .catch((error) => {
          this.show_error("Failed to assign questions: " + Reflect.get(error, "message"));
        })
        .finally(() => {
          this.is_bulk_assigning = false;
        });
    },

    toggle_question_assignment(section_question) {
      Reflect.set(section_question, "is_loading", true);
      const endpoint_url = Reflect.get(section_question, "is_assigned")
        ? BULK_UNASSIGN_QUESTIONS_URL
        : BULK_ASSIGN_QUESTIONS_URL;

      axios
        .post(
          endpoint_url,
          {
            compliance_rule_id: Reflect.get(this.selected_rule, "id"),
            section_question_ids: [Reflect.get(section_question, "id")],
          },
          { headers: this.headers }
        )
        .then((response) => {
          if (Reflect.get(response.data, "status") === "success") {
            this.show_success(Reflect.get(section_question, "is_assigned") ? "Unassigned" : "Assigned");
            this.refresh_data();
          }
        })
        .catch((error) => {
          this.show_error("Failed to update assignment: " + Reflect.get(error, "message"));
        })
        .finally(() => {
          Reflect.set(section_question, "is_loading", false);
        });
    },

    unassign_question(section_question_id) {
      axios
        .post(
          BULK_UNASSIGN_QUESTIONS_URL,
          {
            compliance_rule_id: Reflect.get(this.selected_rule, "id"),
            section_question_ids: [section_question_id],
          },
          { headers: this.headers }
        )
        .then((response) => {
          if (Reflect.get(response.data, "status") === "success") {
            this.show_success("Question unassigned successfully");
            this.refresh_data();
          }
        })
        .catch((error) => {
          this.show_error("Failed to unassign question: " + Reflect.get(error, "message"));
        });
    },

    refresh_data() {
      this.load_assigned_questions();
      this.question_list = this.question_list.map((section_question) => ({
        ...section_question,
        is_assigned: this.check_if_assigned(Reflect.get(section_question, "id")),
      }));
      this.$emit("updated");
    },

    close_modal() {
      this.reset_state();
      this.assigned_forms = [];
      this.$emit("close");
    },

    format_question_type(question_type) {
      if (!question_type || typeof question_type !== "string") {
        return "N/A";
      }
      return question_type
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
    },

    truncate_text(text, max_length = 50) {
      if (!text) {
        return "";
      }
      return text.length > max_length ? text.substring(0, max_length) + "..." : text;
    },
  },
};